'use client';

import React from 'react';
import { Building2, Users, Calculator, ArrowRight, MapPin, FileText, Globe2, Wifi, Coffee, BookOpen } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import Icon from '@/components/ui/AppIcon';
import { BrandText } from '@/components/marketing/BrandText';


export default function ServicesSection() {
  const { t } = useLanguage();

  return (
    <section id="services" className="py-20 lg:py-28 bg-white">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header — stacked: title then intro (no wide split / right-aligned column) */}
        <div className="mb-14 max-w-3xl">
          <p className="text-xs font-semibold text-[#C9A96E] uppercase tracking-widest mb-3">{t('services.tag')}</p>
          <h2 className="font-display text-4xl font-bold text-[#1B3A5C] tracking-tight leading-tight">
            {t('services.title')?.split('\n')?.map((line, i) => (
              <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
            ))}
          </h2>
          <p className="text-slate-500 text-base leading-relaxed mt-6">
            <BrandText text={t('services.desc')} />
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Large featured card - Virtual Office */}
          <article className="lg:col-span-2 relative rounded-2xl bg-[#1B3A5C] p-8 overflow-hidden flex flex-col justify-between min-h-[360px] group">
            <div
              className="pointer-events-none absolute inset-0 z-[1] bg-cover bg-center bg-no-repeat opacity-[0.15]"
              style={{ backgroundImage: 'url(/images/virtual-office-bg.png)' }}
              aria-hidden
            />
            <div className="absolute top-0 right-0 z-[2] w-64 h-64 rounded-full bg-white/4 -translate-y-1/3 translate-x-1/3" />
            <div className="absolute bottom-0 left-0 z-[2] w-48 h-48 rounded-full bg-[#C9A96E]/10 translate-y-1/3 -translate-x-1/3" />

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C9A96E]/20 border border-[#C9A96E]/30 text-[#C9A96E] text-xs font-semibold mb-6">
                {t('services.mostPopular')}
              </div>
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-5">
                <Building2 size={22} className="text-white" />
              </div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">{t('services.virtualOffice.tag')}</p>
              <h3 className="font-display font-bold text-2xl text-white mb-3">{t('services.virtualOffice.title')}</h3>
              <p className="text-slate-300 text-sm leading-relaxed max-w-md mb-2">
                {t('services.virtualOffice.desc')}
              </p>
              <p className="text-slate-400/95 text-xs leading-relaxed max-w-md mb-6">
                {t('services.virtualOffice.taxNote')}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: MapPin, textKey: 'services.virtualOffice.f1' },
                  { icon: MapPin, textKey: 'services.virtualOffice.f5' },
                  { icon: MapPin, textKey: 'services.virtualOffice.f6' },
                  { icon: FileText, textKey: 'services.virtualOffice.f2' },
                  { icon: Globe2, textKey: 'services.virtualOffice.f3' },
                  { icon: BookOpen, textKey: 'services.virtualOffice.f4' },
                ]?.map((f) => {
                  const Icon = f?.icon;
                  return (
                    <div key={f?.textKey} className="flex items-center gap-2 text-xs text-slate-300">
                      <Icon size={13} className="text-[#C9A96E] shrink-0" />
                      {t(f?.textKey)}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative z-10 flex items-center justify-between mt-8 pt-6 border-t border-white/10">
              <div>
                <span className="font-display font-bold text-3xl text-white">{t('services.virtualOffice.price')}</span>
                <span className="text-slate-400 text-sm ml-1">{t('services.virtualOffice.per')}</span>
              </div>
              <a href="#virtual-office" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#C9A96E] group-hover:gap-3 transition-all duration-150">
                {t('services.virtualOffice.learn')} <ArrowRight size={14} />
              </a>
            </div>
          </article>

          {/* Right column - two stacked cards */}
          <div className="flex flex-col gap-5">
            {/* Coworking */}
            <article className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-7 flex flex-col hover:border-[#1B3A5C]/30 hover:shadow-lg transition-all duration-200">
              <div
                className="pointer-events-none absolute inset-0 z-[1] bg-cover bg-center bg-no-repeat opacity-[0.15]"
                style={{ backgroundImage: 'url(/images/coworking-bg.png)' }}
                aria-hidden
              />
              <div
                className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-br from-slate-50 via-[#f3f6fa] to-slate-200/35 opacity-[0.55]"
                aria-hidden
              />
              <div className="relative z-10 flex flex-col flex-1">
                <div className="w-11 h-11 rounded-xl bg-[#C9A96E]/15 flex items-center justify-center mb-4">
                  <Users size={20} className="text-[#8a6830]" />
                </div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">{t('services.coworking.tag')}</p>
                <h3 className="font-display font-bold text-lg text-[#1B3A5C] mb-2">{t('services.coworking.title')}</h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-4 flex-1">
                  {t('services.coworking.desc')}
                </p>
                <div className="flex items-center gap-3 mb-4">
                  {[Wifi, Coffee, Users]?.map((Icon, i) => (
                    <div key={i} className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                      <Icon size={14} className="text-slate-500" />
                    </div>
                  ))}
                  <span className="text-xs text-slate-400">+3 more</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-display font-bold text-xl text-[#1B3A5C]">{t('services.coworking.price')}</span>
                    <span className="text-slate-400 text-xs ml-1">{t('services.coworking.per')}</span>
                  </div>
                  <a href="#coworking" className="inline-flex items-center gap-1 text-xs font-semibold text-[#1B3A5C] group-hover:gap-2 transition-all duration-150">
                    {t('services.coworking.plans')} <ArrowRight size={12} />
                  </a>
                </div>
              </div>
            </article>

            {/* Accounting */}
            <article className="group relative overflow-hidden rounded-2xl bg-emerald-950 border border-emerald-900/50 p-7 flex flex-col hover:border-emerald-700/50 transition-all duration-200">
              <div
                className="pointer-events-none absolute inset-0 z-[1] bg-cover bg-center bg-no-repeat opacity-[0.15]"
                style={{ backgroundImage: 'url(/images/accounting-bg.png)' }}
                aria-hidden
              />
              <div className="relative z-10 flex flex-col flex-1">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/15 flex items-center justify-center mb-4">
                  <Calculator size={20} className="text-emerald-400" />
                </div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-600 mb-1">{t('services.accounting.tag')}</p>
                <h3 className="font-display font-bold text-lg text-white mb-2">{t('services.accounting.title')}</h3>
                <p className="text-sm text-emerald-200/60 leading-relaxed mb-4 flex-1">
                  {t('services.accounting.desc')}
                </p>
                <a href="#accounting-pricing" className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-400 group-hover:gap-2.5 transition-all duration-150">
                  {t('services.accounting.cta')} <ArrowRight size={14} />
                </a>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}